import {
  ArrowLeftRight,
  Bell,
  Bot,
  LayoutDashboard,
  LineChart,
  LogOut,
  Menu,
  PiggyBank,
  Settings,
  Users,
  UsersRound,
  Wallet,
} from "lucide-react";
import { useState, type ComponentType, type ReactNode } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useUnreadCount, useWitnessActionableCount } from "../lib/hooks";
import { ConfirmHost } from "./ConfirmHost";
import { Toaster } from "./Toaster";

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number | string }>;
  end?: boolean;
}

// Frequently-used, top-level destinations stay ungrouped at the top.
const TOP_NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/accounts", label: "Accounts", icon: Wallet },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight },
  { to: "/assistant", label: "Finance Assistant", icon: Bot },
  { to: "/forecast", label: "Forecast", icon: LineChart },
];

// People- and group-oriented features, grouped under one heading.
const PEOPLE_NAV: NavItem[] = [
  { to: "/loans", label: "Loans", icon: PiggyBank },
  { to: "/committees", label: "Committees", icon: Users },
  { to: "/connections", label: "People", icon: UsersRound },
];

const BOTTOM_NAV: NavItem[] = [{ to: "/settings", label: "Settings", icon: Settings }];

export function AppShell(): JSX.Element {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const witnessTodo = useWitnessActionableCount();

  const renderItem = ({ to, label, icon: Icon, end }: NavItem): ReactNode => (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${isActive ? "bg-brand-500 text-white" : "text-slate-600 hover:bg-brand-50"
        }`
      }
    >
      <Icon size={18} />
      <span className="flex-1">{label}</span>
      {to === "/connections" && witnessTodo > 0 && (
        <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
          {witnessTodo > 99 ? "99+" : witnessTodo}
        </span>
      )}
    </NavLink>
  );

  const nav: ReactNode = (
    <nav className="flex flex-1 flex-col gap-1">
      {TOP_NAV.map(renderItem)}

      <p className="mb-1 mt-4 px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        People
      </p>
      {PEOPLE_NAV.map(renderItem)}

      <div className="mt-auto pt-4">{BOTTOM_NAV.map(renderItem)}</div>
    </nav>
  );

  return (
    <div className="flex h-full">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white p-4 lg:flex">
        <Brand />
        {nav}
        <UserFooter name={user?.name ?? ""} onLogout={logout} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 flex lg:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <aside
            className="relative flex w-64 flex-col bg-white p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <Brand />
            {nav}
            <UserFooter name={user?.name ?? ""} onLogout={logout} onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(true)} className="rounded-lg p-1 hover:bg-slate-100">
            <Menu size={22} />
          </button>
          <span className="font-semibold text-brand-500">Vouch</span>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
      <Toaster />
      <ConfirmHost />
    </div>
  );
}

function Brand(): JSX.Element {
  return (
    <div className="mb-6 flex items-center gap-2 px-2">
      <img src="/brand/vouch-mark.webp" alt="" width={96} height={88} className="h-9 w-9 object-contain" />
      <span className="text-lg font-bold text-brand-500">Vouch</span>
    </div>
  );
}

function UserFooter({
  name,
  onLogout,
  onNavigate,
}: {
  name: string;
  onLogout: () => void;
  onNavigate?: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const unread = useUnreadCount();
  return (
    <div className="mt-auto border-t border-slate-200 pt-3">
      <div className="mb-2 flex items-center justify-between gap-2 px-2">
        <span className="min-w-0 truncate text-sm font-medium text-slate-700">{name}</span>
        <button
          onClick={() => {
            navigate("/notifications");
            onNavigate?.();
          }}
          className="relative shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </div>
      <button
        onClick={onLogout}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
      >
        <LogOut size={16} /> Sign out
      </button>
    </div>
  );
}
